import {ApiClient} from '@k2/common/backend/api-client';
import {map} from 'rxjs/operators';
import {AssignmentIdentity} from '../../entities-state/types';

export class AssignmentRealEstateEndpoint {
  constructor(private api: ApiClient) {
  }

  fetchRealEstateServiceOverview = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/service-overview`);
  };

  updateCurrentProgram = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api
      .patch(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/program`, { program: data });
  };

  fetchNewBMAForm = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma/form`)
      .pipe(map(payload => payload.form));
  };

  fetchNewInspectionForm = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/inspection/form`)
      .pipe(map(payload => payload.form));
  };

  addNewBMA = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma`, data);
  };

  updateBma = ({ clientId, assignmentId }: AssignmentIdentity, bmaId, data: any) => {
    return this.api.put(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma/${bmaId}`, data);
  };

  addNewInspection = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/inspections`, data);
  };

  fetchAssignmentBmas = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .getWithoutCache(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma`)
      .pipe(map(payload => payload));
  };

  fetchInspections = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api.getWithoutCache(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/inspections`)
      .pipe(map(payload => payload));
  };

  updateInspection = ({ clientId, assignmentId }: AssignmentIdentity, inspectionId, data: any) => {
    return this.api.put(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/inspections/${inspectionId}`, data);
  };

  fetchBma = ({ clientId, assignmentId }: AssignmentIdentity, bmaId: number) => {
    const url = `/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma/${bmaId}/view`;
    return this.api
      .getWithoutCache(url)
      .pipe(map(payload => payload.form));
  };

  fetchInspection = ({ clientId, assignmentId }: AssignmentIdentity, inspectionId: number) => {
    return this.api.getWithoutCache(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/inspections/${inspectionId}`)
      .pipe(map(payload => payload.form));
  };

  deleteBma = ({ clientId, assignmentId }: AssignmentIdentity, bmaId: number) => {
    return this.api
      .delete(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma/${bmaId}`);
  };

  closeBma = ({ clientId, assignmentId }: AssignmentIdentity, bmaId: number) => {
    return this.api
      .patch(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma/${bmaId}/close`, {});
  };

  deleteInspection = ({ clientId, assignmentId }: AssignmentIdentity, inspectionId: number) => {
    const url = `/staff/clients/${clientId}/assignments/${assignmentId}/bvo/inspections/${inspectionId}`;
    return this.api.delete(url);
  };

  closeInspection = ({ clientId, assignmentId }: AssignmentIdentity, inspectionId: any) => {
    return this.api
      .patch(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/inspections/${inspectionId}/close`, {});
  };

  fetchBrokerCompanyUsers = ({ clientId, assignmentId }: AssignmentIdentity, brokerId: number) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma/brokers/${brokerId}/users`)
      .pipe(map(payload => payload.brokers));
  };

  setBmaIsSelected = ({ clientId, assignmentId }: AssignmentIdentity, bmaId: any, data: any) => {
    const url = `/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma/${bmaId}/selected`;
    return this.api.patch(url, data)
      .pipe(map(payload => payload));
  };
  setIsUsedForValue = ({ clientId, assignmentId }: AssignmentIdentity, bmaId: any, data: any) => {
    const url = `/staff/clients/${clientId}/assignments/${assignmentId}/bvo/bma/${bmaId}/used_for_value`;
    return this.api.patch(url, data)
      .pipe(map(payload => payload));
  };

  fetchOverview = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .getWithoutCache(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/overview`)
      .pipe(map(payload => payload));
  };

  updateOverviewNotes = ({ clientId, assignmentId }: AssignmentIdentity, notes: string) => {
    return this.api
      .patch(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/overview`, { notes: notes })
      .pipe(map(payload => payload.form));
  };

  fetchTitleForm = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .getWithoutCache(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/title`)
      .pipe(map(payload => payload.form));
  };

  saveTitle = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/title`, data);
  };

  fetchListingForm = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api.getWithoutCache(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/listing`)
      .pipe(map(payload => payload.form));
  };

  saveListing = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    data = Object.fromEntries(Object.entries(data).map(([key, value]) => {
      if (value === '') {
        return [key, null];
      }

      return [key, value];
    }));

    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/bvo/listing`, data);
  };

  fetchResaleOfferForm = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .getWithoutCache(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/resale-offer`)
      .pipe(map(payload => payload.form));
  };

  saveResaleOffer = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/resale-offer`, data);
  };

  fetchBuyoutOfferForm = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .getWithoutCache(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/buyout-offer`)
      .pipe(map(payload => payload.form));
  };

  saveBuyoutOffer = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/buyout-offer`, data);
  };

  fetchEquityOrder = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/equity-order`)
      .pipe(map(payload => payload.form));
  };

  saveEquityOrder = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/equity-order`, data)
      .pipe(map(payload => payload.form));
  };

  fetchBuyoutClosing = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/buyout-closing`)
      .pipe(map(payload => payload.form));
  };

  saveBuyoutClosing = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/buyout-closing`, data)
      .pipe(map(payload => payload.form));
  };

  fetchHomesaleBonus = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/homesale-bonus`)
      .pipe(map(payload => payload.form));
  };

  saveHomesaleBonus = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/homesale-bonus`, data)
      .pipe(map(payload => payload.form));
  };

  fetchLossOnSale = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/loss-on-sale`)
      .pipe(map(payload => payload.form));
  };

  saveLossOnSale = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/loss-on-sale`, data)
      .pipe(map(payload => payload.form));
  };

  fetchAppraisal = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/appraisals`)
      .pipe(map(payload => payload.form));
  };

  saveAppraisal = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/appraisals`, data)
      .pipe(map(payload => payload.form));
  };

  fetchAdvanceOfEquity = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/advance-of-equity`)
      .pipe(map(payload => payload.form));
  };

  saveAdvanceOfEquity = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/advance-of-equity`, data)
      .pipe(map(payload => payload.form));
  };

  fetchResaleClosing = ({ clientId, assignmentId }: AssignmentIdentity) => {
    return this.api
      .get(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/resale-closing`)
      .pipe(map(payload => payload.form));
  };

  saveResaleClosing = ({ clientId, assignmentId }: AssignmentIdentity, data: any) => {
    return this.api.post(`/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/resale-closing`, data)
      .pipe(map(payload => payload.form));
  };

  deleteRealEstatePartner = (assignmentId: number, clientId: number, vendorId: number, moduleId: number, moduleType: string) => {
    return this.api.delete(
      `/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/section/${moduleId}/type/${moduleType}/vendor/${vendorId}`
    ).pipe();
  }

  assignContactToModule = (assignmentId: number, clientId: number, vendorId: number, moduleId: number, moduleType: string, contactId: number) => {
    return this.api.patch(
      `/staff/clients/${clientId}/assignments/${assignmentId}/real-estate/section/${moduleId}/type/${moduleType}/vendor/${vendorId}/assign-contact`,
      { contact_id: contactId }).pipe(map(payload => payload));
  }

  postListingMarketingUpdate = (assignmentId: number, clientId: number, vendorId: number, moduleId: number, data: any) => {
    return this.api.post(
      `/staff/clients/${clientId}/assignments/${assignmentId}/bvo/listing/${moduleId}/vendor/${vendorId}/marketing-update`,
      data
    ).pipe(map(payload => payload.form));
  }

  deleteMarketingListingUpdate = (assignmentId: number, clientId: number, moduleId: number, marketingUpdateEntryId: number) => {
    return this.api.delete(
      `/staff/clients/${clientId}/assignments/${assignmentId}/bvo/listing/${moduleId}/marketing-update/${marketingUpdateEntryId}`
    ).pipe(map(payload => payload.form));
  }
}
